import { useNavigate, useRouterState } from "@tanstack/react-router";
import { useState } from "react";
import type { AccountProfile, AuthSessionSummary, PlatformLinkView } from "@nebulize/sdk";

import {
  Button,
  DataTable,
  EmptyState,
  Field,
  Input,
  Page,
  Panel,
  ProviderButton,
  Stack,
  StatusBadge,
  Tabs,
  type Provider,
} from "@/components/ui";

import { Paged } from "../components/Paged";
import { Chip } from "../components/Chips";
import { Facts } from "../components/Text";

import { QueryPanel } from "./components/QueryPanel";
import {
  useAccount,
  useAuthSessions,
  useMachines,
  useRevokeAuthSession,
  useSetHandle,
  useSignOut,
  useStartLink,
  useUnlinkProvider,
} from "./lib/queries";
import { errorText, stamp } from "./lib/present";

const PROVIDERS: readonly Provider[] = ["google", "discord", "facebook", "twitch", "kick"];

const TAB_IDS = ["profile", "providers", "sign-in", "data"] as const;

function activeLinks(profile: AccountProfile | undefined): PlatformLinkView[] {
  return (profile?.links ?? []).filter((one) => one.revoked_at == null);
}

function ProfileTab() {
  const account = useAccount();
  const machines = useMachines();
  const setHandle = useSetHandle();
  const [typed, setTyped] = useState("");

  return (
    <QueryPanel title="Profile" rows={4} query={account}>
      {(profile) => (
        <Stack gap={4}>
          <Facts
            items={[
              { label: "Name", value: profile.display_name },
              { label: "Account", value: profile.account_id, mono: true },
              { label: "Email", value: profile.email ?? "none" },
              { label: "Created", value: stamp(profile.created_at) },
              {
                label: "State",
                value: (
                  <StatusBadge tone={profile.state === "active" ? "success" : "warning"}>
                    {profile.state}
                  </StatusBadge>
                ),
              },
              { label: "Machines", value: String(machines.data?.length ?? 0) },
            ]}
          />
          <form
            className="cx-listbar"
            onSubmit={(event) => {
              event.preventDefault();
              setHandle.mutate(typed.trim());
            }}
          >
            <Field label="Handle">
              {({ id }) => (
                <Input
                  id={id}
                  mono
                  value={typed}
                  placeholder={profile.handle ?? "unset"}
                  onChange={(event) => {
                    setTyped(event.target.value);
                  }}
                />
              )}
            </Field>
            <Button
              type="submit"
              size="sm"
              variant="secondary"
              loading={setHandle.isPending}
              disabled={typed.trim() === ""}
            >
              Save
            </Button>
          </form>
          <div aria-live="polite">
            {setHandle.isError ? (
              <StatusBadge tone="danger">{errorText(setHandle.error)}</StatusBadge>
            ) : null}
          </div>
        </Stack>
      )}
    </QueryPanel>
  );
}

function ProvidersTab() {
  const account = useAccount();
  const startLink = useStartLink();
  const unlink = useUnlinkProvider();

  const links = activeLinks(account.data);
  const last = links.length <= 1;
  const held = new Set(links.map((one) => one.provider));

  return (
    <Stack gap={5}>
      <QueryPanel title="Linked providers" rows={3} query={account}>
        {() =>
          links.length === 0 ? (
            <EmptyState bare headline="None" />
          ) : (
            <DataTable
              caption="Providers linked to this account"
              density="comfortable"
              columns={[
                { key: "provider", header: "Provider", emphasis: true },
                { key: "display_name", header: "Name" },
                {
                  key: "linked_at",
                  header: "Linked",
                  muted: true,
                  render: (row: PlatformLinkView) => stamp(row.linked_at),
                },
                {
                  key: "scopes",
                  header: "Scopes",
                  render: (row: PlatformLinkView) => (
                    <div className="cx-chips">
                      {(row.scopes ?? []).map((one) => (
                        <Chip key={one}>{one}</Chip>
                      ))}
                    </div>
                  ),
                },
                {
                  key: "unlink",
                  header: "",
                  align: "right",
                  width: "130px",
                  render: (row: PlatformLinkView) => (
                    <Button
                      size="sm"
                      variant="destructive"
                      aria-label={
                        last ? `Unlink ${row.provider}, last sign-in method` : `Unlink ${row.provider}`
                      }
                      disabled={last || unlink.isPending}
                      onClick={() => {
                        unlink.mutate(row.provider);
                      }}
                    >
                      Unlink
                    </Button>
                  ),
                },
              ]}
              rows={links}
              emptyHeadline="None"
            />
          )
        }
      </QueryPanel>

      <Panel title="Link another">
        <Stack gap={3}>
          <div className="cx-chips">
            {PROVIDERS.filter((one) => !held.has(one)).map((provider) => (
              <ProviderButton
                key={provider}
                provider={provider}
                action="Link"
                loading={startLink.isPending && startLink.variables === provider}
                onClick={() => {
                  startLink.mutate(provider, {
                    onSuccess: (started) => {
                      window.location.assign(started.authorization_url);
                    },
                  });
                }}
              />
            ))}
          </div>
          <div aria-live="polite">
            {startLink.isError ? (
              <StatusBadge tone="danger">{errorText(startLink.error)}</StatusBadge>
            ) : null}
            {unlink.isError ? (
              <StatusBadge tone="danger">{errorText(unlink.error)}</StatusBadge>
            ) : null}
          </div>
        </Stack>
      </Panel>
    </Stack>
  );
}

function SignInTab() {
  const sessions = useAuthSessions();
  const revoke = useRevokeAuthSession();
  const signOut = useSignOut();

  return (
    <QueryPanel
      title="Browser sessions"
      rows={3}
      query={sessions}
      action={
        <Stack direction="row" gap={3} align="center">
          <Button
            size="sm"
            variant="secondary"
            loading={revoke.isPending && revoke.variables === null}
            onClick={() => {
              revoke.mutate(null);
            }}
          >
            Sign out everywhere else
          </Button>
          <Button
            size="sm"
            variant="destructive"
            loading={signOut.isPending}
            onClick={() => {
              signOut.mutate();
            }}
          >
            Sign out
          </Button>
        </Stack>
      }
    >
      {(rows) =>
        rows.length === 0 ? (
          <EmptyState bare headline="None" />
        ) : (
          <Paged
            rows={rows}
            pageSize={10}
            placeholder="Find a session"
            match={(row: AuthSessionSummary, needle: string) =>
              `${row.browser_family ?? ""} ${row.source_address ?? ""}`
                .toLowerCase()
                .includes(needle)
            }
          >
            {(page) => (
              <DataTable
                caption="Browser sessions signed in to this account"
                density="comfortable"
                columns={[
                  {
                    key: "browser_family",
                    header: "Browser",
                    emphasis: true,
                    render: (row: AuthSessionSummary) => row.browser_family ?? "unknown",
                  },
                  {
                    key: "source_address",
                    header: "Address",
                    mono: true,
                    render: (row: AuthSessionSummary) => row.source_address ?? "unknown",
                  },
                  {
                    key: "last_seen_at",
                    header: "Last seen",
                    muted: true,
                    render: (row: AuthSessionSummary) => stamp(row.last_seen_at),
                  },
                  {
                    key: "expires_at",
                    header: "Expires",
                    muted: true,
                    render: (row: AuthSessionSummary) => stamp(row.expires_at),
                  },
                  {
                    key: "is_current",
                    header: "This browser",
                    render: (row: AuthSessionSummary) =>
                      row.is_current === true ? (
                        <StatusBadge tone="info">this browser</StatusBadge>
                      ) : null,
                  },
                  {
                    key: "revoke",
                    header: "",
                    align: "right",
                    width: "120px",
                    render: (row: AuthSessionSummary) => (
                      <Button
                        size="sm"
                        variant="destructive"
                        aria-label={`Revoke ${row.browser_family ?? "session"} ${row.auth_session_id}`}
                        disabled={row.is_current === true || revoke.isPending}
                        onClick={() => {
                          revoke.mutate(row.auth_session_id);
                        }}
                      >
                        Revoke
                      </Button>
                    ),
                  },
                ]}
                rows={page}
                emptyHeadline="None"
              />
            )}
          </Paged>
        )
      }
    </QueryPanel>
  );
}

function DataTab() {
  return (
    <Stack gap={5}>
      <Panel title="Export">
        <Stack gap={3}>
          <EmptyState bare headline="Not available" />
          <div className="cx-verbs">
            <Button size="sm" variant="secondary" disabled>
              Request an export
            </Button>
          </div>
        </Stack>
      </Panel>
      <Panel title="Delete this account">
        <Stack gap={3}>
          <EmptyState bare headline="Not available" />
          <div className="cx-verbs">
            <Button size="sm" variant="destructive" disabled>
              Request deletion
            </Button>
          </div>
        </Stack>
      </Panel>
    </Stack>
  );
}

/** Profile, providers, sign-in security and data, each addressable by the tab in the URL. */
export function ConsoleAccount() {
  const navigate = useNavigate();
  const search = useRouterState({ select: (state) => state.location.search }) as Record<
    string,
    unknown
  >;
  const asked = typeof search["tab"] === "string" ? search["tab"] : "";
  const active: string = TAB_IDS.find((one) => one === asked) ?? "profile";

  return (
    <Page
      title="Account"
      tabs={
        <Tabs
          label="Account"
          value={active}
          onChange={(id) => {
            void navigate({ to: "/console/account", search: { tab: id } });
          }}
          tabs={[
            { id: "profile", label: "Profile", content: <ProfileTab /> },
            { id: "providers", label: "Providers", content: <ProvidersTab /> },
            { id: "sign-in", label: "Sign-in", content: <SignInTab /> },
            { id: "data", label: "Data", content: <DataTab /> },
          ]}
        />
      }
    />
  );
}
